import React, { useState, useCallback, useReducer } from "react";
import PropTypes from "prop-types";
import { injectIntl, FormattedMessage } from "react-intl";
import withStyles from "components/with-styles";
import styles from "./styles/timeline.css.js";
import Icon from "components/icon";
import Button from "components/buttons";
import Dialog from "components/dialog";
import { isOverlapped, DEFAULT_DURATION } from "./timeline-utils";
import DataGrid, { DataGridItem } from "components/data-grid";
import FormTextField from "components/form/form-textfield";
import uniqueId from "lodash/uniqueId";
import FormHelperText from "@material-ui/core/FormHelperText";
import { DebouncingInputsProvider } from "components/form";

export const newSegmentReducer = (state, action) => {
  switch (action.type) {
    case "start":
      return { ...state, start: parseInt(action.value || 0, 10) };
    case "end":
      return { ...state, end: parseInt(action.value || 0, 10) };
    case "error":
      return { ...state, error: action.value };
    case "reset":
      return { start: 0, end: DEFAULT_DURATION, error: null };
    default:
      throw new Error();
  }
};

const TimelineButtonAdd = ({ intl, element, classes, onChange, type, period }) => {
  const [open, setOpen] = useState(false);
  const [state, dispatch] = useReducer(newSegmentReducer, { start: 0, end: DEFAULT_DURATION, error: null });

  const handleCloseDialog = useCallback(
    e => {
      e.stopPropagation();
      setOpen(false);
      dispatch({ type: "reset" });
    },
    [setOpen],
  );

  const handleStartChange = name => value => dispatch({ type: "start", value });
  const handleEndChange = name => value => dispatch({ type: "end", value });

  const handleSegmentAdd = event => {
    event.preventDefault();

    if (isOverlapped({ start: state.start, end: state.end, segments: element.segments })) {
      return dispatch({ type: "error", value: "timeline.segment.add.overlaps" });
    }

    if (state.start >= state.end) {
      return dispatch({ type: "error", value: "timeline.segment.add.startAfterEnd" });
    }

    onChange({
      ...element,
      segments: [...element.segments, { id: uniqueId(), type, start: state.start, end: state.end }],
    });

    setOpen(false);
    dispatch({ type: "reset" });
  };

  return element.showAlways ? (
    <div className={classes.timeline__add_button} />
  ) : (
    <DebouncingInputsProvider disabled>
      <Button
        key={element.layoutId}
        className={classes.timeline__add_button}
        onClick={() => setOpen(true)}
        variant="outlined"
        size="small"
      >
        <Icon name="add" className={classes.timeline__add_icon} />
      </Button>
      <Dialog
        open={open}
        onAccept={handleSegmentAdd}
        showModalAccept
        showModalCloseCross
        onClose={handleCloseDialog}
        title={intl.formatMessage({ id: "timeline.segment.add.title" })}
        paperProps={{
          onClick: e => {
            e.preventDefault();
            e.stopPropagation();
          },
        }}
      >
        <DataGridItem big style={{ padding: 12 }}>
          <DataGrid>
            <DataGridItem style={{ padding: "0px 12px" }}>
              <FormTextField
                type="number"
                name="start"
                inputProps={{ min: 0 }}
                value={state.start}
                onChange={handleStartChange}
                onFocus={e => e.target.select()}
                label={intl.formatMessage({ id: "timeline.element.start" })}
              />
            </DataGridItem>
            <DataGridItem style={{ padding: "0px 12px" }}>
              <FormTextField
                type="number"
                name="end"
                inputProps={{ min: 0 }}
                value={state.end}
                onChange={handleEndChange}
                onFocus={e => e.target.select()}
                label={intl.formatMessage({ id: "timeline.element.end" })}
              />
            </DataGridItem>
          </DataGrid>
          {state.error && (
            <FormHelperText error>
              <FormattedMessage id={state.error} />
            </FormHelperText>
          )}
        </DataGridItem>
      </Dialog>
    </DebouncingInputsProvider>
  );
};

TimelineButtonAdd.propTypes = {
  /**
   * Elemento del timeline
   */
  element: PropTypes.shape({
    layoutId: PropTypes.string.isRequired,
    showAlways: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        start: PropTypes.number,
        end: PropTypes.number,
      }),
    ),
  }),
  /*
   * Callback que se llama con el elemento actualizado
   */
  onChange: PropTypes.func,
  /*
   * Período que representa el timeline (en segundos)
   */
  period: PropTypes.number,
  /*
   * Tipo de schedule
   */
  type: PropTypes.string,
};

export default withStyles(styles)(injectIntl(TimelineButtonAdd));
